// @file This store manages screen recording in a more minimal sense compared to surface_screen_recorder_store.ts which it is based on
/**
 * This store is used to handle the case where a user is recording their screen for a single post (no draft/comment handling)
 * note: this store assumes there will only be one screen recording session at a time for a user
 */

import { ScreenRecorderStatus } from '@@/bits/screen_recorder'
import { UploadPurpose } from '@@/bits/uploader'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export type ScreenRecorderSaveHandler = (
  file: File,
  options?: {
    trimVideoAfterUpload?: { start: number; end: number }
    recordingDuration?: number
    uploadPurpose?: UploadPurpose
  },
) => void

export type ScreenRecorderCloseHandler = () => void

export type ScreenRecorderShowHandler = () => void

export interface ScreenRecorderHandlers {
  onShow?: ScreenRecorderShowHandler
  onSave: ScreenRecorderSaveHandler
  onClose?: ScreenRecorderCloseHandler
}

export const useScreenRecorderStore = defineStore('screenRecorderStore', () => {
  // #region: visibility for screen recorder modal
  const isVisible = ref(false)
  const saveRecordingHandler = ref<ScreenRecorderSaveHandler | null>(null)
  const closeHandler = ref<ScreenRecorderCloseHandler | null>(null)
  const showHandler = ref<ScreenRecorderShowHandler | null>(null)

  const isRecording = computed(
    () => status.value === ScreenRecorderStatus.Recording || status.value === ScreenRecorderStatus.Paused,
  )

  /**
   * Show the screen recorder with the provided handlers.
   * @param handlers - Object containing onShow (optional), onSave (required), and onClose (optional) handlers
   */
  const showScreenRecorder = (handlers: ScreenRecorderHandlers): void => {
    showHandler.value = handlers.onShow ?? null
    saveRecordingHandler.value = handlers.onSave
    closeHandler.value = handlers.onClose ?? null

    // Run show handler (e.g., to capture draft state)
    showHandler.value?.()

    isVisible.value = true
    status.value = ScreenRecorderStatus.Connecting
  }

  const hideScreenRecorder = (): void => {
    isVisible.value = false
    status.value = ScreenRecorderStatus.Hidden
    saveRecordingHandler.value = null
    closeHandler.value = null
    showHandler.value = null
    returnToUrl.value = null
  }

  /**
   * Handle saving a recording by calling the registered handler.
   * Called directly by the ScreenRecorder component.
   */
  const handleSaveRecording = (
    file: File,
    options?: {
      trimVideoAfterUpload?: { start: number; end: number }
      recordingDuration?: number
      uploadPurpose?: UploadPurpose
    },
  ): void => {
    if (saveRecordingHandler.value) {
      saveRecordingHandler.value(file, options)
    }
    hideScreenRecorder()
  }

  /**
   * Handle closing the recorder without saving.
   * Calls the registered close handler (if any) to restore draft state, then hides the recorder.
   */
  const handleClose = (): void => {
    closeHandler.value?.()
    hideScreenRecorder()
  }
  // #endregion

  // #region status for screen recorder modal
  const status = ref<ScreenRecorderStatus>(ScreenRecorderStatus.Hidden)
  const setStatus = (newStatus: ScreenRecorderStatus): void => {
    status.value = newStatus
  }

  // Navigate back to original URL when user stops recording (enters preview mode)
  // This ensures the component is mounted before save, so the handler works correctly
  watch(status, (newStatus) => {
    if (newStatus === ScreenRecorderStatus.Previewing && returnToUrl.value) {
      if (window.location.href !== returnToUrl.value) {
        window.history.pushState(null, '', returnToUrl.value)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
      // Clear returnToUrl since we've navigated back
      returnToUrl.value = null
    }
  })
  // #endregion

  // #region: z index override for screen recorder modal
  const zIndexOverride = ref<number | null>(null)
  const setZIndexOverride = (zIndex: number | null): void => {
    zIndexOverride.value = zIndex
  }
  // #endregion

  // #region: return-to-URL for navigation during recording
  /**
   * URL to navigate back to when recording stops (enters preview mode).
   * Set this when starting a recording from a specific context (e.g., discussion message input).
   */
  const returnToUrl = ref<string | null>(null)
  const setReturnToUrl = (url: string | null): void => {
    returnToUrl.value = url
  }
  // #endregion

  return {
    // visibility
    isVisible,
    isRecording,
    zIndexOverride,
    setZIndexOverride,
    showScreenRecorder,
    hideScreenRecorder,

    // callbacks
    handleSaveRecording,
    handleClose,

    // status
    status,
    setStatus,

    // return-to-URL
    returnToUrl,
    setReturnToUrl,
  }
})
